import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components/macro'
import FlexibleDialog, {
  propTypes as dialogPropTypes,
} from 'components/molecules/flexible-dialog'
import PropTypes from 'prop-types'
import LoadingButton from 'components/atoms/loading-button'
import Typography from 'components/molecules/typography'
import { useDispatch } from 'react-redux'
import { publishPortal } from 'redux/actions/data'
import toast from 'utilities/toast'
import { AdvertisementPortalIdEnum } from 'config/enums'
import CarEntryHeader from 'components/molecules/car-entry-header'
import { media } from 'utilities/styled'

const Controls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ theme, $noMarginTop }) =>
    $noMarginTop ? 0 : theme.sizings.lvl4};
  padding: ${({ theme }) => theme.sizings.lvl2} 0;
`

const StyledFlexibleDialog = styled(FlexibleDialog)`
  .MuiDialog-paper {
    width: ${({ theme }) => theme.sizeByFactor(75)};

    > h2 {
      padding-bottom: 0;
    }
  }
`

const StyledCarEntryHeader = styled(CarEntryHeader)`
  height: ${({ theme }) => theme.sizeByFactor(10.5)};
  width: 100%;
  background-color: ${({ theme }) => theme.colors.brandGolf};
  margin-bottom: ${({ theme }) => theme.sizings.lvl2};

  img {
    height: ${({ theme }) => theme.sizeByFactor(10.5)};
    width: ${({ theme }) => theme.sizeByFactor(14)};
    object-fit: cover;
    flex-shrink: 0;
  }

  > div:first-child {
    min-width: 0;
  }

  [data-test-e2e='car-title'] {
    overflow: hidden;
    min-width: 0;

    > * {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    > *:nth-child(n + 3) {
      display: none;
    }
  }

  ${media.desktop`
    margin-bottom: 0;

    [data-test-e2e="car-title"] {
      overflow: visible;

      > * {
        white-space: normal;
        overflow: visible;
        text-overflow: unset;
      }

      > *:nth-child(n + 3) {
        display: block;
      }
    }
  `}
`

const AdvertisementBulkActionsDialog = ({
  onClose,
  selectedCarIds,
  selectedPortal,
  selectedPortalName,
  selectedCar,
  ...restProps
}) => {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const dispatch = useDispatch()

  const handleSubmit = () => {
    setIsSubmitting(true)
    selectedCarIds.forEach((carId) => {
      dispatch(
        publishPortal('portalPublish', {
          auto_id: Number(carId),
          portal_ids: [Number(selectedPortal)],
        }),
      )
        .then((data) => {
          setIsSubmitting(false)
          toast.success(
            t('advertisementView.success', { portal: selectedPortalName ?? selectedPortal }),
          )
          onClose && onClose()
        })
        .catch((error) => {
          console.error(error)
          setIsSubmitting(false)
        })
    })
  }

  return (
    <StyledFlexibleDialog
      title={t(
        selectedCarIds.length === 1
          ? 'advertisementView.bulkActionsTitleSingleSelection'
          : 'advertisementView.bulkActionsTitle',
        {
          count: selectedCarIds.length,
          portal: selectedPortalName ?? selectedPortal,
        },
      )}
      disableBackdropClick={isSubmitting}
      onClose={onClose}
      open
      content={
        <>
          {selectedCarIds.length === 1 && selectedCar && (
            <StyledCarEntryHeader
              imagePath={selectedCar.image}
              brand={selectedCar.brand}
              model={selectedCar.model}
              type={selectedCar.type}
              licensePlate={selectedCar.licensePlate}
              mileage={selectedCar.mileage}
              showImageOnMobile
            />
          )}
          {[AdvertisementPortalIdEnum.MARKTPLAATS, AdvertisementPortalIdEnum.MARKTPLAATS].includes(String(selectedPortal)) && (
            <Typography type="ExplanationParagraph">
              {t('advertisementView.extraCostsDescription')}
            </Typography>
          )}
          <Controls $noMarginTop>
            <LoadingButton
              level="option"
              onClick={onClose}
              text={t('cancel')}
              disabled={isSubmitting}
              noPadding
              data-test-e2e="button-cancel"
            />
            <LoadingButton
              level="cta"
              isLoading={isSubmitting}
              type="submit"
              onClick={handleSubmit}
              data-test-e2e="button-save"
            >
              {t('advertisementView.advertiseOnPortal')}
            </LoadingButton>
          </Controls>
        </>
      }
      {...restProps}
      data-test-e2e="advertisement-bulk-actions-dialog"
    />
  )
}

AdvertisementBulkActionsDialog.propTypes = {
  title: PropTypes.string,
  content: PropTypes.node,
  selectedCarIds: PropTypes.array,
  selectedPortal: PropTypes.string,
  selectedPortalName: PropTypes.string,
  selectedCar: PropTypes.object,
  ...dialogPropTypes,
}

export default AdvertisementBulkActionsDialog
